// CmcUsageWidget/CmcUsageWidget.jsx
// Extends PainterBase — draggable card, no cardDataListener subscription:
// CmcUsageEngine notifies via ComponentNotifier, same as FearGreedWidget.
import React from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

// components / controls
import JxTitleBar from '../../../JxControls/JxTitleBar/JxTitleBar';

// core
import PainterBase from '../Core/PainterBase';

// local
import cmcUsageEngine from './CmcUsageEngine';
import ComponentNotifier from '../../../services/wss/ComponentNotifier';
import CmcUsageProgressBar from './CmcUsageProgressBar';
import { fromNow } from '../../../services/utils/DateFormat';
import './CmcUsageWidget.css';

class CmcUsageWidget extends PainterBase {
    constructor(props) {
        super(props);
        this.name = `CmcUsageWidget_${props.meta.target}`; // required by JxForm — unique per instance
        this.schema = null;

        this.state = { ...this.state, data: null };
    }

    componentDidMount() {
        super.componentDidMount();
        this._unsubscribe = ComponentNotifier.subscribe('CmcUsageEngine', (e) => this.onPaint(e.data.state));

        const existing = cmcUsageEngine.getState();
        if (existing) this.onPaint(existing);
    }

    componentWillUnmount() {
        if (this._unsubscribe) this._unsubscribe();
        super.componentWillUnmount();
    }

    componentDidUpdate(prevProps) {
        super.componentDidUpdate(prevProps);
    }

    getDefaultGeo() {
        return { top: 120, left: 120, width: 300, height: 240 };
    }

    // PainterBase contract — called on every ComponentNotifier 'dataChanged' event.
    onPaint(state) {
        this.setState({ data: state });
    }

    /**
     * @returns {object[]} JxTitleBar actions — pin toggle + close.
     */
    getTitleBarActions() {
        return [
            {
                icon: this.state.isPinned ? <PushPinIcon sx={{ fontSize: 14 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 14 }} />,
                title: this.state.isPinned ? 'Unpin' : 'Pin on top',
                onClick: this.togglePin,
            },
            {
                icon: <CloseIcon sx={{ fontSize: 16 }} />,
                title: 'Close',
                onClick: () => this.handleClose(-1),
            },
        ];
    }

    render() {
        if (!this.props.open) return null;

        const { data } = this.state;
        const panel = (
            <div {...this.panelProps} style={this.panelStyle} className={`cmc-usage-widget ${this.panelActiveClass} ${this.panelPinnedClass}`}>
                <JxTitleBar
                    id={`${this.name}_titlebar`}
                    title="cmc usage"
                    variant="toolwindow"
                    isDraggable={true}
                    dragHandleProps={this.dragHandleProps}
                    actions={this.getTitleBarActions()}
                />
                <div className="cuw-body">{data ? this._renderData(data) : <div className="cuw-loading">waiting for data…</div>}</div>
            </div>
        );

        return createPortal(panel, document.body);
    }

    _renderData(data) {
        const { month, day, rateLimitPerMinute, minuteRequestsLeft } = data;

        return (
            <>
                <div className="cuw-hero">
                    <div className="cuw-hero-row">
                        <span className="cuw-hero-label">Month credits used</span>
                        <span className="cuw-hero-value">
                            {month.used} <span className="cuw-hero-limit">/ {month.limit}</span>
                        </span>
                    </div>
                    <CmcUsageProgressBar pct={month.pct} color={month.band.color} />
                    <div className="cuw-hero-footer">
                        <span>{month.left} left</span>
                        <span>{month.pct}%</span>
                    </div>
                </div>

                <div className="cuw-grid">
                    <div className="cuw-stat">
                        <div className="cuw-stat-label">Rate limit</div>
                        <div className="cuw-stat-value">
                            {rateLimitPerMinute}
                            <span className="cuw-stat-unit">/min</span>
                        </div>
                        <div className="cuw-stat-sub">{minuteRequestsLeft} left this minute</div>
                    </div>
                    <div className={`cuw-stat ${day.depleted ? 'cuw-stat-warning' : ''}`}>
                        <div className="cuw-stat-label">Day credits</div>
                        <div className="cuw-stat-value" style={{ color: day.band.color }}>
                            {day.used}
                            <span className="cuw-stat-unit">used</span>
                        </div>
                        <div className="cuw-stat-sub">{day.depleted ? 'day limit reached' : `${day.left} left`}</div>
                    </div>
                </div>

                <div className="cuw-footer">
                    <i className="ti ti-clock" aria-hidden="true" />
                    <span>updated {fromNow(data.lastUpdated)}</span>
                </div>
            </>
        );
    }
}

export default CmcUsageWidget;
